import cx from 'classnames';
import React, { useState, useCallback, useEffect, startTransition, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MdClose } from 'react-icons/md';
import { ImagesSection_MainQuery, ImagesSection_MainQueryVariables, Media, MediaRole, MediaType } from '../../../../codegen/graphql';
import { gql, useMutation, useSuspenseQuery } from '@apollo/client';
import { arrayMove } from '@dnd-kit/sortable';
import { SortableImageList } from './SortableImageList';
import { uploadImages, deleteImage, updateImageOrder, setMediaRoles, downloadAdImagesZip } from '../../../utils/imageApi';
import { DragEndEvent } from '@dnd-kit/core';
import { useImageTools } from '../../ImageTools/ImageProvider';
import Button from '../../common/Button';
import { getTranslatedValue, TranslatedValue, Translations, useTranslationEditsMap } from '../../common/TranslatedValue';
import { Image } from '../../ImageTools/ImageProvider';
import { useModalRouter } from '../../../ModalRouter_context';
import { useScreenContext } from '../../Screen';
import { ButtonGroup } from '../../common/ButtonGroup';

interface ImagesSectionProps {
  adData: NonNullable<ImagesSection_MainQuery['ad']>;
  refetch: () => void;
  onStartEditing: (medias: Media[]) => void;
}

type SelectedRole = MediaRole | 'UNASSIGNED' | 'VIDEO' | 'ALL' | null;
type SelectedFilter = { type: 'role', key: SelectedRole } | { type: 'variation', key: string } | null;
type SelectionMode = 'edit' | 'batchTranslation';

const SELECTION_MODE_CONFIG: Record<SelectionMode, { help: string; confirmLabel: (count: number) => string }> = {
  edit: {
    help: 'Select the images you want to edit.',
    confirmLabel: (count) => `Edit Selected (${count})`,
  },
  batchTranslation: {
    help: 'Select the images you want to translate.',
    confirmLabel: (count) => `Translate Selected (${count})`,
  },
};

interface DragState {
  isDragging: boolean;
  isValid: boolean;
}

export function ImagesSection({
  adData,
  refetch,
  onStartEditing,
}: ImagesSectionProps) {
  /* Global tools used */
  const {
    openImageSelector,
    openLightbox,
    closeImageSelector,
    closeLightbox,
  } = useImageTools();

  const screenContext = useScreenContext();
  const modalRouter = useModalRouter();

  /* Main query data */
  const { data: mainQueryData } = useSuspenseQuery<
    ImagesSection_MainQuery,
    ImagesSection_MainQueryVariables
  >(IMAGES_SECTION_MAIN_QUERY, {
    variables: { adId: adData.id },
  });

  const medias = (mainQueryData.ad?.medias || []);
  const allImages = medias.filter(media => media.type === MediaType.Image);
  const variationTypes = mainQueryData.ad?.variationTypes || [];
  const translationEdits = useTranslationEditsMap();
  const [updateVariationType] = useMutation(IMAGES_SECTION_UPDATE_VARIATION_TYPE);
  const [updateVariationTypeValue] = useMutation(IMAGES_SECTION_UPDATE_VARIATION_TYPE_VALUE);

  /* UI state */
  const [selectedFilter, setSelectedFilter] = useState<SelectedFilter>(null);
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    isValid: false
  });
  const [selectionMode, setSelectionMode] = useState<SelectionMode | null>(null);
  const [selectedMedias, setSelectedMedias] = useState<Media[]>([]);
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const screenContextRef = useRef(screenContext);
  screenContextRef.current = screenContext;
  
  /* Event handlers */
  const handleMediaUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    try {
      await uploadImages(files, adData.id, []);  // Upload without any roles
      startTransition(() => {
        refetch();
      });
    } catch (error) {
      console.error('Error uploading media:', error);
    }
  }, [adData.id, refetch]);

  const handleRoleSelection = async (selectedMedias: Image[]) => {
    if (!selectedFilter) return;

    try {
      if (selectedFilter.type === 'role') {
        const role = selectedFilter.key;
        if (role === MediaRole.PrimaryImage) {
          // For primary image, we just need to handle one image
          const selectedMedia = selectedMedias[0];
          if (selectedMedia) {
            // Remove PrimaryImage role from all other images
            const currentPrimaryImage = medias.find(m => 
              m.roles?.includes(MediaRole.PrimaryImage)
            );
            if (currentPrimaryImage) {
              await setMediaRoles(currentPrimaryImage.id!, 
                currentPrimaryImage.roles!.filter(r => r !== MediaRole.PrimaryImage)
              );
            }
            // Set the new primary image
            await setMediaRoles(selectedMedia.id!, [MediaRole.PrimaryImage]);
          }
        } else if (role && role !== 'UNASSIGNED' && role !== 'VIDEO' && role !== 'ALL') {
          // For other roles:
          // 1. Find all medias that currently have this role
          const mediasWithRole = medias.filter(m => m.roles?.includes(role as MediaRole));
          
          // 2. Remove role from medias that are not in the selection anymore
          await Promise.all(
            mediasWithRole
              .filter(m => !selectedMedias.find(sm => sm.id === m.id))
              .map(async (media) => {
                await setMediaRoles(
                  media.id!,
                  media.roles!.filter(r => r !== role)
                );
              })
          );

          // 3. Add role to newly selected medias
          await Promise.all(
            selectedMedias.map(async (media) => {
              const newRoles = [...(media.roles || [])];
              if (!newRoles.includes(role as MediaRole)) {
                newRoles.push(role as MediaRole);
              }
              await setMediaRoles(media.id!, newRoles);
            })
          );
        }
      } else {
        // Variation type value filter
        // Implementation needed
      }

      startTransition(() => {
        refetch();
      });
      closeImageSelector();
    } catch (error) {
      console.error('Error updating media roles:', error);
    }
  };

  const startSelection = useCallback((mode: SelectionMode) => {
    setSelectionMode(mode);
    setSelectedMedias([]);
  }, []);

  const exitSelection = useCallback(() => {
    setSelectionMode(null);
    setSelectedMedias([]);
  }, []);

  const confirmSelection = useCallback(() => {
    if (!selectionMode || selectedMedias.length === 0) return;

    const mediasToConfirm = selectedMedias;
    setSelectionMode(null);
    setSelectedMedias([]);

    if (selectionMode === 'edit') {
      onStartEditing(mediasToConfirm);
      return;
    }

    const mediaIds = mediasToConfirm
      .filter(media => media.type === MediaType.Image && media.id)
      .map(media => media.id);
    if (mediaIds.length > 0) {
      modalRouter.push({
        s: 'nanoBananaMediasTranslation',
        a: { mediaIds },
      });
    }
  }, [selectionMode, selectedMedias, onStartEditing, modalRouter]);

  const toggleMediaSelection = (media: Media) => {
    if (!selectionMode) return;
    if (selectionMode === 'batchTranslation' && media.type !== MediaType.Image) return;

    setSelectedMedias(prev => {
      const isSelected = prev.some(m => m.id === media.id);
      if (isSelected) {
        return prev.filter(m => m.id !== media.id);
      }
      return [...prev, media];
    });
  };

  const removeMedia = async (mediaId: string) => {
    try {
      await deleteImage(mediaId);
      await refetch();
    } catch (error) {
      console.error('Error removing media:', error);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = medias.findIndex(media => media.id === active.id);
    const newIndex = medias.findIndex(media => media.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(medias, oldIndex, newIndex);

    try {
      await updateImageOrder(
        adData.id,
        newOrder.map((media, index) => ({
          id: media.id!,
          position: index
        }))
      );
      startTransition(() => {
        refetch();
      });
    } catch (error) {
      console.error('Error updating media order:', error);
    }
  };

  const handleMediaClick = (media: Media) => {
    if (selectionMode) {
      toggleMediaSelection(media);
    } else {
      openLightbox({
        medias: allImages,
        initialIndex: medias.findIndex(m => m.id === media.id)
      });
    }
  };

  // Add useEffect for page-wide drag and drop
  useEffect(() => {
    const handlePageDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragState(prev => ({
        ...prev,
        isDragging: true,
        isValid: e.dataTransfer?.types.includes('Files') || false
      }));
    };

    const handlePageDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handlePageDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      // Only set isDragging to false if we're leaving the page (not entering a child element)
      if (!e.relatedTarget || !(e.relatedTarget as Element).closest('html')) {
        setDragState(prev => ({
          ...prev,
          isDragging: false
        }));
      }
    };

    const handlePageDrop = async (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      setDragState({
        isDragging: false,
        isValid: false
      });

      const files = Array.from(e.dataTransfer?.files || []);
      if (files.length === 0) return;

      try {
        await uploadImages(files, adData.id, []); // Upload without any roles
        startTransition(() => {
          refetch();
        });
      } catch (error) {
        console.error('Error uploading media:', error);
      }
    };

    // Add event listeners to document
    document.addEventListener('dragenter', handlePageDragEnter);
    document.addEventListener('dragover', handlePageDragOver);
    document.addEventListener('dragleave', handlePageDragLeave);
    document.addEventListener('drop', handlePageDrop);

    // Cleanup function
    return () => {
      document.removeEventListener('dragenter', handlePageDragEnter);
      document.removeEventListener('dragover', handlePageDragOver);
      document.removeEventListener('dragleave', handlePageDragLeave);
      document.removeEventListener('drop', handlePageDrop);
    };
  }, [adData.id, refetch]); // Dependencies for useEffect

  const roleButtons = [
    { key: 'ALL' as const, label: 'All' },
    { key: 'VIDEO' as const, label: 'Videos' },
    { key: MediaRole.PrimaryImage, label: 'Primary Image' },
    { key: MediaRole.Main, label: 'Main Images' },
    { key: MediaRole.Description, label: 'Description Images' },
    { key: 'UNASSIGNED' as const, label: 'Unassigned Images' },
  ];

  const hasImageMedias = medias.some(m => m.type === MediaType.Image);

  useLayoutEffect(() => {
    return () => {
      screenContextRef.current.nav.setActions([]);
    };
  }, []);

  useLayoutEffect(() => {
    if (selectionMode) {
      const { confirmLabel } = SELECTION_MODE_CONFIG[selectionMode];
      screenContext.nav.setActions({
        left: [
          {
            label: confirmLabel(selectedMedias.length),
            preset: 'primary',
            disabled: selectedMedias.length === 0,
            onClick: confirmSelection,
          },
          {
            label: 'Cancel',
            preset: 'white',
            onClick: exitSelection,
          },
        ],
      });
      return;
    }

    screenContext.nav.setActions({
        left: [
        {
          label: 'Edit Images',
          preset: 'primary',
          onClick: () => startSelection('edit'),
        },
        {
          label: 'Translate image batch',
          preset: 'primary',
          onClick: () => startSelection('batchTranslation'),
        },
        {
          label: 'Add Medias or drag and drop',
          preset: 'primary',
          onClick: () => fileInputRef.current?.click(),
        },
      ],
      right: [
        {
          label: isDownloadingZip ? 'Preparing…' : 'Download images (ZIP)',
          preset: 'white',
          onClick: handleDownloadImagesZip,
          disabled: !hasImageMedias || isDownloadingZip,
        },
      ]
    });
  }, [selectionMode, selectedMedias.length, confirmSelection, startSelection, exitSelection]);

  const handleDownloadImagesZip = async () => {
    try {
      setIsDownloadingZip(true);
      const blob = await downloadAdImagesZip(adData.id);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      const safeTitle = adData.title['FR'].replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '').toLowerCase().substring(0, 40);
      link.download = `${safeTitle || 'ad'}_images.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Error downloading images zip:', error);
      alert('Failed to download images. Please try again.');
    } finally {
      setIsDownloadingZip(false);
    }
  };

  if (dragState.isDragging) {
    return (
      <div className="bg-white shadow-sm rounded-lg p-6">
        <div style={{ pointerEvents: 'none' }} className={`flex items-center justify-center h-64 rounded-lg border-2 border-dashed ${
          dragState.isValid ? 'border-blue-500 bg-blue-50' : 'border-red-500 bg-red-50'
        }`}>
          <div className={`text-xl font-medium ${
            dragState.isValid ? 'text-blue-500' : 'text-red-500'
          }`}>
            {dragState.isValid ? 'Drop medias here' : 'Invalid files'}
          </div>
        </div>
      </div>
    );
  }

  return (<>
    <input
      ref={fileInputRef}
      type="file"
      multiple
      accept="image/*,video/*"
      onChange={handleMediaUpload}
      className="hidden"
    />
    {selectionMode && createPortal(
      <div className="fixed bottom-6 left-1/2 z-50 flex w-[min(32rem,calc(100%-2rem))] -translate-x-1/2 items-start gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-xl">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900">
            {SELECTION_MODE_CONFIG[selectionMode].help}
          </p>
          <p className="mt-1 text-sm text-gray-500">
            {selectedMedias.length} selected
          </p>
        </div>
        <button
          type="button"
          onClick={exitSelection}
          className="shrink-0 rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
          aria-label="Exit selection"
        >
          <MdClose size={20} />
        </button>
      </div>,
      document.body,
    )}
    <div className="bg-white shadow-sm rounded-lg p-6">

    <div className="space-y-4">
      {/* Role filters */}
      <ButtonGroup
        options={roleButtons.map(({ key, label }) => {
          const isSelected = selectedFilter?.type === 'role' && selectedFilter.key === key;
          const filteredCount = filteredMedias({ type: 'role', key: key }, medias).length;
          const isUnassignedWithMultiple = key === 'UNASSIGNED' && filteredCount > 1;

          return {
            value: key,
            label: label + ' (' + filteredCount + ')',
          }
        })}
        value={selectedFilter?.type === 'role' ? selectedFilter.key : null}
        onChange={value => {
          if (value === (selectedFilter?.type === 'role' ? selectedFilter.key : null)) {
            setSelectedFilter(null);
          } else {
            setSelectedFilter({ type: 'role', key: value });
          }
        }}
        disabled={!hasImageMedias}
      />

      {/* Variation type value filters */}
      {variationTypes.filter(vType => vType.id === adData.typeIdUsedForImages).map(vType => {
        return (
          <ButtonGroup
            key={vType.id}
            label={getTranslatedValue(
              translationEdits.value(`type:${vType.id}`, vType.type as unknown as Translations)
            )}
            options={vType.typeValues?.map(value => {
              return {
                value: value.id,
                label: getTranslatedValue(
                  translationEdits.value(`type:${vType.id}:${value.id}`, value.value as unknown as Translations)
                ),
              }
            })}
            value={selectedFilter?.type === 'variation' ? selectedFilter.key : null}
            onChange={value => {
              if (value === (selectedFilter?.type === 'variation' ? selectedFilter.key : null)) {
                setSelectedFilter(null);
              } else {
                setSelectedFilter({ type: 'variation', key: value });
              }
            }}
          />
        )
      })}
    </div>

    {selectedFilter?.type === 'role' && selectedFilter.key !== 'UNASSIGNED' && selectedFilter.key !== 'VIDEO' && (
      <div className="mt-4">
        <button
          onClick={() => openImageSelector({
            images: allImages,
            initialSelection: filteredMedias(selectedFilter, medias),
            onSelectionComplete: handleRoleSelection,
            singleSelect: selectedFilter?.type === 'role' && selectedFilter.key === MediaRole.PrimaryImage
          })}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          Select Images
        </button>
      </div>
    )}

    <div className="mt-8">
      <SortableImageList
        medias={filteredMedias(selectedFilter, medias)}
        isSelecting={selectionMode !== null}
        selectedRole={null}
        selectedMedias={selectedMedias}
        onMediaClick={handleMediaClick}
        onRemove={removeMedia}
        onDragEnd={handleDragEnd}
      />
    </div>
    </div>
  </>);
}


/*
 * Data queries
 */
const IMAGES_SECTION_UPDATE_VARIATION_TYPE = gql`
  mutation ImagesSection_UpdateVariationType($id: ID!, $input: UpdateAdVariationTypeInput!) {
    updateAdVariationType(id: $id, input: $input) {
      id
      type
    }
  }
`;

const IMAGES_SECTION_UPDATE_VARIATION_TYPE_VALUE = gql`
  mutation ImagesSection_UpdateVariationTypeValue($id: ID!, $input: UpdateAdVariationTypeValueInput!) {
    updateAdVariationTypeValue(id: $id, input: $input) {
      id
      value
    }
  }
`;

export const IMAGES_SECTION_MAIN_QUERY = gql`
  query ImagesSection_Main($adId: ID!) {
    ad(id: $adId) {
      id
      title
      medias {
        id
        originalFilepath
        editedFilepath
        editorData
        size
        url
        originalUrl
        type
        roles
        position
        width
        height
        translations {
          lang
          langMediaActive
        }
        variationTypeValues {
          id
          value
          type {
            id
            type
          }
        }
      }
      typeIdUsedForImages
      variationTypes {
        id
        type
        typeValues {
          id
          value
        }
      }
    }
  }
`;


/*
 * Utils
 */
const filteredMedias = (filter: SelectedFilter, medias: NonNullable<ImagesSection_MainQuery['ad']>['medias']) => {
  if (!filter) return medias;

  if (filter.type === 'role') {
    const role = filter.key;
    
    if (role === 'ALL') {
      return medias;
    }

    if (role === 'UNASSIGNED') {
      return medias.filter(media => (
        (!media.roles || media.roles.length === 0) && 
        media.type === MediaType.Image &&
        (!media.variationTypeValues || media.variationTypeValues.length === 0)
      ));
    }

    if (role === 'VIDEO') {
      return medias.filter(media => media.type === MediaType.Video);
    }

    return medias.filter(media => (
      media.roles?.includes(role as MediaRole) &&
      media.type === MediaType.Image
    ));
  }

  // Variation type value filter
  return medias.filter(media => 
    media?.variationTypeValues?.some(vtv => 
      vtv.type.id + '-' + vtv.id === filter.key
    )
  );
};